import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import TopNav from "@/components/TopNav";
import KpiCards from "@/components/KpiCards";
import LeadTable from "@/components/LeadTable";
import LeadFormDialog from "@/components/LeadFormDialog";
import ReportsPanel from "@/components/ReportsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { STATUS_META } from "@/lib/constants";
import { ExecutivesProvider, useExecutives } from "@/context/ExecutivesContext";
import AdminPanel from "@/components/AdminPanel";
import DeviceApprovals from "@/components/DeviceApprovals";
import ActivityLog from "@/components/ActivityLog";
import ManagerReporting from "@/components/ManagerReporting";
import { Plus, Search, RefreshCw } from "lucide-react";

const POLL_MS = 15000;

function ExecOverview({ perExec }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {perExec.map((ex) => (
        <Card key={ex.username} className="p-5 border-slate-200 shadow-sm" data-testid={`exec-overview-${ex.username}`}>
          <div className="flex items-center justify-between mb-3">
            <p className="font-heading font-semibold text-slate-900">{ex.name}</p>
            <span className="text-xs font-mono text-slate-400">{ex.total} total</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {["pending", "success", "failed"].map((s) => (
              <div key={s} className={`rounded-lg border px-2 py-2 text-center ${STATUS_META[s].badge}`}>
                <p className="font-mono font-bold text-lg leading-none">{ex[s]}</p>
                <p className="text-[10px] uppercase tracking-wider mt-1 font-medium">{STATUS_META[s].label}</p>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isManagerial = user.role === "sales_manager" || user.role === "management";
  const isManagement = user.role === "management";

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [execFilter, setExecFilter] = useState("all");
  const [sort, setSort] = useState("created_desc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [synced, setSynced] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const filtersRef = useRef({});

  filtersRef.current = { search, statusFilter, execFilter, sort };

  const fetchData = useCallback(async () => {
    const { search, statusFilter, execFilter, sort } = filtersRef.current;
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== "all") params.status = statusFilter;
    if (isManagerial && execFilter !== "all") params.executive = execFilter;
    const [sb, sd] = (sort || "created_desc").split("_");
    params.sort_by = sb === "event" ? "event_date" : "created_at";
    params.sort_dir = sd === "asc" ? "asc" : "desc";
    const [leadsRes, statsRes] = await Promise.all([
      api.get("/leads", { params }),
      api.get("/dashboard/stats"),
    ]);
    setLeads(leadsRes.data);
    setStats(statsRes.data);
    setSynced("just now");
  }, [isManagerial]);

  useEffect(() => {
    fetchData();
  }, [fetchData, search, statusFilter, execFilter, sort]);

  useEffect(() => {
    const id = setInterval(() => fetchData(), POLL_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  // "synced X ago" ticker
  useEffect(() => {
    if (synced !== "just now") return;
    const start = Date.now();
    const id = setInterval(() => {
      const secs = Math.round((Date.now() - start) / 1000);
      setSynced(secs < 5 ? "just now" : `${secs}s ago`);
    }, 5000);
    return () => clearInterval(id);
  }, [synced]);

  const manualRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const onSaved = (lead) => fetchData();
  const onChanged = (updated, deletedId) => {
    if (deletedId) {
      setLeads((ls) => ls.filter((l) => l.id !== deletedId));
      fetchData();
    } else if (updated) {
      setLeads((ls) => ls.map((l) => (l.id === updated.id ? updated : l)));
      fetchData();
    }
  };

  const openCreate = () => {
    setEditLead(null);
    setDialogOpen(true);
  };
  const openEdit = (lead) => {
    setEditLead(lead);
    setDialogOpen(true);
  };

  return (
    <ExecutivesProvider>
    <div className="min-h-screen bg-[#F8FAFC]">
      <TopNav synced={synced} />

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              {isManagerial ? "Management Overview" : "My Leads Dashboard"}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isManagerial
                ? "Full visibility across all sales executives"
                : `Leads assigned to ${user.name}`}
            </p>
          </div>
          <Button onClick={openCreate} className="bg-[#1E3A8A] hover:bg-[#172554]" data-testid="create-lead-button">
            <Plus className="h-4 w-4 mr-1.5" /> New Lead
          </Button>
        </div>

        <KpiCards totals={stats?.totals} />

        {isManagerial ? (
          <Tabs defaultValue="leads" className="space-y-5">
            <TabsList data-testid="dashboard-tabs" className="flex flex-wrap h-auto justify-start">
              <TabsTrigger value="leads" data-testid="tab-leads">Lead Master</TabsTrigger>
              <TabsTrigger value="overview" data-testid="tab-overview">Executive Breakdown</TabsTrigger>
              <TabsTrigger value="reports" data-testid="tab-reports">Reports & Export</TabsTrigger>
              {isManagement && (
                <>
                  <TabsTrigger value="mgr-report" data-testid="tab-mgr-report">Sales Manager Reporting</TabsTrigger>
                  <TabsTrigger value="admin" data-testid="tab-admin">User Management</TabsTrigger>
                  <TabsTrigger value="devices" data-testid="tab-devices">Device Approvals</TabsTrigger>
                  <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value="leads">
              <LeadWorkspace
                leads={leads}
                onChanged={onChanged}
                onEdit={openEdit}
                search={search}
                setSearch={setSearch}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                execFilter={execFilter}
                setExecFilter={setExecFilter}
                showExecFilter
                sort={sort}
                setSort={setSort}
                onRefresh={manualRefresh}
                refreshing={refreshing}
              />
            </TabsContent>

            <TabsContent value="overview">
              {stats?.per_executive && <ExecOverview perExec={stats.per_executive} />}
            </TabsContent>

            <TabsContent value="reports">
              <ReportsPanel />
            </TabsContent>

            {isManagement && (
              <TabsContent value="admin">
                <AdminPanel />
              </TabsContent>
            )}
            {isManagement && (
              <TabsContent value="mgr-report">
                <ManagerReporting />
              </TabsContent>
            )}
            {isManagement && (
              <TabsContent value="devices">
                <DeviceApprovals />
              </TabsContent>
            )}
            {isManagement && (
              <TabsContent value="activity">
                <ActivityLog />
              </TabsContent>
            )}
          </Tabs>
        ) : (
          <LeadWorkspace
            leads={leads}
            onChanged={onChanged}
            onEdit={openEdit}
            search={search}
            setSearch={setSearch}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            sort={sort}
            setSort={setSort}
            onRefresh={manualRefresh}
            refreshing={refreshing}
          />
        )}
      </main>

      <LeadFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        lead={editLead}
        onSaved={onSaved}
      />
    </div>
    </ExecutivesProvider>
  );
}

function LeadWorkspace({
  leads,
  onChanged,
  onEdit,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  execFilter,
  setExecFilter,
  showExecFilter,
  sort,
  setSort,
  onRefresh,
  refreshing,
}) {
  const { executives } = useExecutives();
  return (
    <Card className="border-slate-200 shadow-sm overflow-hidden">
      <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search customer, contact, source..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white"
            data-testid="lead-search-input"
          />
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.keys(STATUS_META).map((s) => (
                <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showExecFilter && (
            <Select value={execFilter} onValueChange={setExecFilter}>
              <SelectTrigger className="w-[170px]" data-testid="exec-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Executives</SelectItem>
                {executives.map((ex) => (
                  <SelectItem key={ex.username} value={ex.username}>{ex.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[190px]" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_desc">Created: Newest first</SelectItem>
              <SelectItem value="created_asc">Created: Oldest first</SelectItem>
              <SelectItem value="event_desc">Event date: Latest first</SelectItem>
              <SelectItem value="event_asc">Event date: Earliest first</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={onRefresh} data-testid="manual-refresh-button" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>
      <LeadTable leads={leads} onChanged={onChanged} onEdit={onEdit} />
    </Card>
  );
}
